import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { useBulkAssignShift, usePayrollPolicies } from '../../../services/httpServices/queries';
import { DatePicker } from '../../../components/ui/DatePicker';
import type { Shift } from '../../../services/httpServices/hrService';
import {
  WEEKDAYS,
  addDays,
  employeeName,
  endOfMonth,
  formatISODate,
  formatShiftTime,
  formatWeeklyOffDays,
  getWeekStart,
  resolveWeeklyOffDays,
  shiftColorStyle,
  startOfMonth,
} from './utils';
import { CalendarDays, CheckSquare, Loader2, Search, Square, X } from 'lucide-react';

type PeriodMode = 'week' | 'month' | 'custom';

interface BulkAssignModalProps {
  open: boolean;
  onClose: () => void;
  employees: any[];
  shifts: Shift[];
}

const ALL_WEEKDAYS = [0, 1, 2, 3, 4, 5, 6];

export function BulkAssignModal({ open, onClose, employees, shifts }: BulkAssignModalProps) {
  const bulkAssign = useBulkAssignShift();
  const { data: payrollPoliciesData } = usePayrollPolicies();
  const payrollPolicies = useMemo(
    () => payrollPoliciesData?.data || [],
    [payrollPoliciesData],
  );

  const [shiftId, setShiftId] = useState('');
  const [selectedEmployees, setSelectedEmployees] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState('');
  const [periodMode, setPeriodMode] = useState<PeriodMode>('week');
  const [anchorDate, setAnchorDate] = useState(() => formatISODate(new Date()));
  const [customFrom, setCustomFrom] = useState(() => formatISODate(new Date()));
  const [customTo, setCustomTo] = useState(() => formatISODate(new Date()));
  const [weekdays, setWeekdays] = useState<number[]>(ALL_WEEKDAYS);
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (!open) return;
    setShiftId((prev) => prev || shifts.find((s) => s.isActive)?.id || shifts[0]?.id || '');
    setSelectedEmployees(new Set());
    setSearch('');
    setNotes('');
    setWeekdays(ALL_WEEKDAYS);
    setPeriodMode('week');
  }, [open, shifts]);

  const { dateFrom, dateTo } = useMemo(() => {
    const anchor = new Date(`${anchorDate}T00:00:00`);
    if (periodMode === 'week') {
      const start = getWeekStart(anchor);
      return { dateFrom: formatISODate(start), dateTo: formatISODate(addDays(start, 6)) };
    }
    if (periodMode === 'month') {
      return { dateFrom: formatISODate(startOfMonth(anchor)), dateTo: formatISODate(endOfMonth(anchor)) };
    }
    return { dateFrom: customFrom, dateTo: customTo };
  }, [periodMode, anchorDate, customFrom, customTo]);

  const daysInRange = useMemo(() => {
    const from = new Date(`${dateFrom}T00:00:00`);
    const to = new Date(`${dateTo}T00:00:00`);
    if (isNaN(from.getTime()) || isNaN(to.getTime()) || from > to) return 0;
    let count = 0;
    for (const d = new Date(from); d <= to; d.setDate(d.getDate() + 1)) {
      if (weekdays.includes(d.getDay())) count += 1;
    }
    return count;
  }, [dateFrom, dateTo, weekdays]);

  const assignmentsPerEmployee = useMemo(() => {
    const counts = new Map<string, number>();
    const from = new Date(`${dateFrom}T00:00:00`);
    const to = new Date(`${dateTo}T00:00:00`);
    if (isNaN(from.getTime()) || isNaN(to.getTime()) || from > to) return counts;

    for (const employeeId of selectedEmployees) {
      const employee = employees.find((item) => item.id === employeeId);
      const offDays = new Set(resolveWeeklyOffDays(employee, payrollPolicies));
      let count = 0;
      for (const cursor = new Date(from); cursor <= to; cursor.setDate(cursor.getDate() + 1)) {
        const day = cursor.getDay();
        if (!weekdays.includes(day)) continue;
        if (offDays.has(day)) continue;
        count += 1;
      }
      counts.set(employeeId, count);
    }
    return counts;
  }, [dateFrom, dateTo, weekdays, selectedEmployees, employees, payrollPolicies]);

  const filteredEmployees = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return employees;
    return employees.filter((e) => {
      const name = employeeName(e).toLowerCase();
      const dept = (e.department?.name || '').toLowerCase();
      const code = (e.employeeCode || '').toLowerCase();
      return name.includes(term) || dept.includes(term) || code.includes(term);
    });
  }, [employees, search]);

  const totalAssignments = useMemo(
    () => [...assignmentsPerEmployee.values()].reduce((sum, count) => sum + count, 0),
    [assignmentsPerEmployee],
  );

  if (!open) return null;

  const selectedShift = shifts.find((s) => s.id === shiftId);

  const toggleEmployee = (id: string) => {
    setSelectedEmployees((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    setSelectedEmployees((prev) => {
      if (filteredEmployees.every((e) => prev.has(e.id))) {
        const next = new Set(prev);
        filteredEmployees.forEach((e) => next.delete(e.id));
        return next;
      }
      return new Set([...prev, ...filteredEmployees.map((e) => e.id)]);
    });
  };

  const toggleWeekday = (value: number) => {
    setWeekdays((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value].sort()
    );
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!shiftId) return toast.error('Please select a shift');
    if (selectedEmployees.size === 0) return toast.error('Select at least one employee');
    if (daysInRange === 0) return toast.error('The selected range has no matching days');
    if (totalAssignments === 0)
      return toast.error('Every selected day is a weekly off day for these employees');

    bulkAssign.mutate(
      {
        employeeIds: [...selectedEmployees],
        shiftId,
        dateFrom,
        dateTo,
        weekdays: weekdays.length === 7 ? undefined : weekdays,
        skipWeeklyOffDays: true,
        notes: notes || undefined,
      },
      {
        onSuccess: (res) => {
          toast.success(res.message || `Shift assigned to ${totalAssignments} record(s)`);
          onClose();
        },
        onError: (err: any) => toast.error(err?.response?.data?.message || 'Failed to assign shifts'),
      }
    );
  };

  const allFilteredSelected =
    filteredEmployees.length > 0 && filteredEmployees.every((e) => selectedEmployees.has(e.id));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-surface rounded-xl border border-border shadow-lg w-full max-w-3xl max-h-[92vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-border">
          <div className="flex items-center gap-2">
            <CalendarDays size={18} className="text-primary" />
            <h2 className="text-lg font-semibold text-text">Bulk Assign Shift</h2>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-background rounded-lg transition-colors">
            <X size={18} className="text-text-muted" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-4 space-y-5">
          {shifts.length === 0 ? (
            <div className="p-3 bg-warning/10 border border-warning/20 rounded-lg text-warning text-sm">
              No shifts available. Create a shift in the <span className="font-medium">Shifts</span> tab first.
            </div>
          ) : (
            <>
              {/* Shift */}
              <div>
                <label className="block text-sm font-medium text-text mb-1">Shift *</label>
                <select
                  value={shiftId}
                  onChange={(e) => setShiftId(e.target.value)}
                  className="w-full px-3 py-2 border border-border rounded-lg bg-background text-text focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  {shifts
                    .filter((s) => s.isActive)
                    .map((shift) => (
                      <option key={shift.id} value={shift.id}>
                        {shift.name} ({formatShiftTime(shift.startTime)} - {formatShiftTime(shift.endTime)})
                      </option>
                    ))}
                </select>
                {selectedShift && (
                  <div
                    className="mt-2 inline-flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-xs font-medium border"
                    style={shiftColorStyle(selectedShift.color)}
                  >
                    <span className="w-2 h-2 rounded-full" style={{ backgroundColor: selectedShift.color }} />
                    {selectedShift.name}
                  </div>
                )}
              </div>

              {/* Period */}
              <div className="border border-border rounded-xl p-4 space-y-3">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <span className="text-sm font-medium text-text">Apply for</span>
                  <div className="flex bg-background border border-border rounded-lg overflow-hidden">
                    {([
                      { key: 'week', label: 'Full Week' },
                      { key: 'month', label: 'Full Month' },
                      { key: 'custom', label: 'Custom Range' },
                    ] as { key: PeriodMode; label: string }[]).map((option) => (
                      <button
                        key={option.key}
                        type="button"
                        onClick={() => setPeriodMode(option.key)}
                        className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                          periodMode === option.key
                            ? 'bg-primary text-white'
                            : 'text-text-muted hover:text-text hover:bg-surface'
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>

                {periodMode === 'custom' ? (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-text-muted mb-1">From</label>
                      <DatePicker
                        value={customFrom}
                        onChange={setCustomFrom}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-text-muted mb-1">To</label>
                      <DatePicker
                        value={customTo}
                        min={customFrom || undefined}
                        onChange={setCustomTo}
                      />
                    </div>
                  </div>
                ) : (
                  <div>
                    <label className="block text-xs font-medium text-text-muted mb-1">
                      {periodMode === 'week' ? 'Any date in the week' : 'Any date in the month'}
                    </label>
                    <DatePicker value={anchorDate} onChange={setAnchorDate} />
                  </div>
                )}

                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="text-xs font-medium text-text-muted">Days of week</label>
                    <div className="flex items-center gap-2 text-xs">
                      <button
                        type="button"
                        onClick={() => setWeekdays([1, 2, 3, 4, 5])}
                        className="text-primary hover:underline"
                      >
                        Mon–Fri
                      </button>
                      <button
                        type="button"
                        onClick={() => setWeekdays(ALL_WEEKDAYS)}
                        className="text-primary hover:underline"
                      >
                        All
                      </button>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {WEEKDAYS.map((day) => {
                      const active = weekdays.includes(day.value);
                      return (
                        <button
                          key={day.value}
                          type="button"
                          onClick={() => toggleWeekday(day.value)}
                          className={`px-2.5 py-1 rounded-lg text-xs font-medium border transition-colors ${
                            active
                              ? 'bg-primary/10 text-primary border-primary/30'
                              : 'bg-background text-text-muted border-border hover:text-text'
                          }`}
                        >
                          {day.short}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <p className="text-xs text-text-muted">
                  Range: <span className="text-text font-medium">{dateFrom}</span> →{' '}
                  <span className="text-text font-medium">{dateTo}</span> ·{' '}
                  <span className="text-text font-medium">{daysInRange}</span> day(s) per employee
                  <br />
                  Each employee's paid weekly off days are skipped automatically.
                </p>
              </div>

              {/* Employees */}
              <div className="border border-border rounded-xl overflow-hidden">
                <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-border bg-background/50">
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={toggleAll}
                      className="text-text-muted hover:text-primary transition-colors"
                      title="Select all"
                    >
                      {allFilteredSelected ? <CheckSquare size={16} /> : <Square size={16} />}
                    </button>
                    <span className="text-sm font-medium text-text">
                      Employees ({selectedEmployees.size} selected)
                    </span>
                  </div>
                  <div className="relative">
                    <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-text-muted" />
                    <input
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder="Search employees"
                      className="pl-8 pr-3 py-1.5 text-sm border border-border rounded-lg bg-surface text-text focus:outline-none focus:ring-2 focus:ring-primary w-56"
                    />
                  </div>
                </div>
                <div className="max-h-56 overflow-y-auto divide-y divide-border">
                  {filteredEmployees.length === 0 ? (
                    <p className="px-4 py-8 text-center text-sm text-text-muted">No employees found</p>
                  ) : (
                    filteredEmployees.map((employee) => {
                      const checked = selectedEmployees.has(employee.id);
                      const offDays = resolveWeeklyOffDays(employee, payrollPolicies);
                      return (
                        <label
                          key={employee.id}
                          className="flex items-center gap-3 px-4 py-2 hover:bg-background/50 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleEmployee(employee.id)}
                            className="accent-[color:var(--color-primary)]"
                          />
                          <div className="min-w-0">
                            <p className="text-sm text-text truncate">{employeeName(employee)}</p>
                            <p className="text-xs text-text-muted truncate">
                              {employee.employeeCode}
                              {employee.department?.name ? ` · ${employee.department.name}` : ''}
                              {offDays.length ? ` · Off: ${formatWeeklyOffDays(offDays)}` : ''}
                            </p>
                          </div>
                        </label>
                      );
                    })
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-text mb-1">Notes</label>
                <input
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Optional notes applied to every assigned day"
                  className="w-full px-3 py-2 border border-border rounded-lg bg-background text-text focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
            </>
          )}

          <div className="flex items-center justify-between gap-3 pt-4 border-t border-border">
            <p className="text-xs text-text-muted">
              {totalAssignments > 0
                ? `${totalAssignments} attendance record(s) will be created or updated`
                : 'Select a shift, period and employees'}
            </p>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm text-text-muted hover:text-text transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={bulkAssign.isPending || shifts.length === 0}
                className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium flex items-center gap-2 disabled:opacity-50"
              >
                {bulkAssign.isPending && <Loader2 size={16} className="animate-spin" />}
                Apply to {selectedEmployees.size || 0} employee(s)
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
